import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer, PolarRadiusAxis } from "recharts";
import useSeo from "@/lib/useSeo";
import { motion } from "framer-motion";
import { Mail, Check } from "lucide-react";

export default function BoardResults() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [displayVri, setDisplayVri] = useState(0);
  const prevCountRef = useRef(0);

  const emailSummary = async () => {
    setEmailing(true);
    try {
      await api.post(`/board-sessions/${code}/email-summary`, null, { params: { token } });
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 4000);
    } finally { setEmailing(false); }
  };

  useSeo({
    title: `Board Session ${code} — Langwood360`,
    description: "Live Board VRI and Alignment Variance dashboard.",
    path: `/board/${code}/results`,
    noindex: true,
  });

  useEffect(() => {
    const load = () =>
      api.get(`/board-sessions/${code}/results`, { params: { token } })
        .then(({ data }) => {
          const previousCount = prevCountRef.current;
          const expected = data.expected_participants;
          if (expected && previousCount < expected && data.count >= expected) {
            // Just crossed the finish line — trigger the theatrical reveal.
            setJustCompleted(true);
            // count-up animation for Board VRI
            const target = data.board_vri || 0;
            const duration = 2600;
            const start = performance.now();
            const step = (t) => {
              const p = Math.min(1, (t - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setDisplayVri(Math.round(target * eased));
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
            setTimeout(() => setJustCompleted(false), 6000);
          }
          prevCountRef.current = data.count;
          setData(data);
        })
        .catch(e => setError(e.response?.data?.detail || e.message));
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [code, token]);

  if (error) return <div className="min-h-screen bg-linen"><Navbar /><div className="max-w-4xl mx-auto p-12 text-heritage" data-testid="board-results-error">{error}</div></div>;
  if (!data) return <div className="min-h-screen bg-linen"><Navbar /><div className="max-w-4xl mx-auto p-12 tick-label">Loading…</div></div>;

  const noSubs = data.count === 0;
  const av = data.alignment_variance;
  const avColor = av?.label === "Low" ? "text-orange" : av?.label === "Moderate" ? "text-slate" : "text-heritage";

  return (
    <div className="min-h-screen bg-linen">
      <Navbar />
      {justCompleted && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto px-6 lg:px-12 pt-4"
          data-testid="board-reveal-banner"
        >
          <div className="border-2 border-orange bg-linen p-4 flex items-center justify-between">
            <div className="tick-label-accent">✦ The last director has responded — this is your Board Index</div>
            <div className="tick-label">Live reveal</div>
          </div>
        </motion.div>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="tick-label-accent mb-4">Board Session · {code}</div>
            <h1 className="display-serif text-4xl md:text-6xl">{data.organisation}</h1>
            <div className="tick-label mt-2">Facilitated by {data.facilitator_name}</div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to={`/board/${code}/directorates?token=${token}`}
              className="btn-ghost"
              data-testid="view-directorates-link"
            >
              Cross-Directorate Report →
            </Link>
            <button data-testid="board-email-summary" onClick={emailSummary} disabled={emailing || emailSent} className="btn-ghost">
              {emailSent ? <><Check className="w-3.5 h-3.5" strokeWidth={1.5}/> Sent</>
                         : <><Mail className="w-3.5 h-3.5" strokeWidth={1.5}/> {emailing ? "Sending…" : "Email me the summary"}</>}
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-oxford/10">
          <div className="bg-linen p-10">
            <div className="tick-label">Board VRI</div>
            <div className="mt-4 font-mono text-7xl text-oxford leading-none" data-testid="board-vri">
              {noSubs ? "—" : (justCompleted ? displayVri : data.board_vri)}
            </div>
            {!noSubs && <div className="mt-2 tick-label">{data.board_band.band}</div>}
          </div>
          <div className="bg-linen p-10">
            <div className="tick-label-accent">Alignment Variance</div>
            <div className={`mt-4 display-serif text-6xl ${avColor}`} data-testid="board-av">
              {noSubs ? "—" : av.label}
            </div>
            {!noSubs && <div className="mt-2 tick-label">σ = {av.stddev} · range {av.range}</div>}
            <p className="mt-3 text-sm text-charcoal">
              How much your board members disagree with each other. Low variance means shared reality. High variance means Clarity has not yet preceded Alignment.
            </p>
          </div>
          <div className="bg-linen p-10">
            <div className="tick-label">Participation</div>
            <div className="mt-4 font-mono text-7xl text-oxford leading-none">
              {data.count}<span className="text-oxford/40 text-4xl">/{data.expected_participants || "?"}</span>
            </div>
            <div className="mt-2 tick-label">responses received</div>
            <div className="mt-6">
              <div className="tick-label mb-2">Invite link</div>
              <div className="font-mono text-xs text-charcoal break-all">{window.location.origin}/diagnostic?board={code}</div>
            </div>
          </div>
        </div>
      </section>

      {!noSubs && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 border border-oxford/15 bg-linen p-8" data-testid="board-spider">
            <div className="tick-label-accent mb-4">Aggregated Dimensions</div>
            <ResponsiveContainer width="100%" height={420}>
              <RadarChart data={data.dimensions.map(d => ({ dim: d.dimension, score: d.score }))} outerRadius="70%">
                <PolarGrid gridType="polygon" stroke="#0E2148" strokeOpacity={0.18} />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "#0E2148", fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#6D82B4", fontSize: 10 }} axisLine={false} />
                <Radar dataKey="score" stroke="#E86A20" fill="#E86A20" fillOpacity={0.22} strokeWidth={1.8} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="lg:col-span-5">
            <div className="tick-label-accent mb-4">Dimension variance</div>
            <div className="space-y-4">
              {data.dimensions.map(d => (
                <div key={d.dimension} className="border border-oxford/15 bg-linen p-4 flex items-baseline justify-between">
                  <div>
                    <div className="display-serif text-xl">{d.dimension}</div>
                    <div className="tick-label mt-1">min {d.min} · max {d.max}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-2xl text-oxford">{d.score}%</div>
                    <div className="tick-label text-orange">σ {d.variance}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {!noSubs && (
        <SessionTimeline participants={data.participants} cascade={data.cascade || []} expected={data.expected_participants} />
      )}

      {!noSubs && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="tick-label-accent mb-6">Individual submissions</div>
          <div className="border border-oxford/15 bg-linen divide-y divide-oxford/10">
            {data.participants.map(p => (
              <div key={p.id} className="p-5 flex items-baseline justify-between">
                <div>
                  <div className="text-oxford">{p.name}</div>
                  <div className="tick-label mt-1">{p.role || "—"}</div>
                </div>
                <div className="font-mono text-2xl text-oxford">{p.vri}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.cascade && data.cascade.length > 0 && (
        <LeadershipCascade cascade={data.cascade} organisation={data.organisation} />
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="rule mb-8" />
        <div className="flex gap-4">
          <Link to="/board/new" className="btn-ghost">Create another session</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}


// ---------- Leadership Cascade ----------
function LeadershipCascade({ cascade, organisation }) {
  return (
    <section className="border-t border-oxford/10 bg-parchment mt-16" data-testid="leadership-cascade">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <div className="tick-label-accent mb-6">A Third Dimension</div>
          <h2 className="display-serif text-4xl md:text-6xl leading-tight max-w-4xl">
            The Leadership <em className="italic text-orange">Cascade</em>.
          </h2>
          <div className="rule-orange my-8" />
          <p className="max-w-2xl text-lg text-charcoal leading-relaxed">
            For each board director, we compare their own Value Realisation Index to the average of their direct reports. The <em>Alignment Gap</em> reveals whether reality flows cleanly down the organisation — or whether the story changes at every level.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {cascade.map((c, i) => (
            <CascadeCard key={c.member_id} c={c} idx={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-16 pt-8 border-t border-oxford/15 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm"
        >
          <div>
            <div className="tick-label-accent mb-2">Aligned</div>
            <p className="text-charcoal">Gap ≤ 8 pts. Team and director are seeing the same organisation. Trust the diagnostic.</p>
          </div>
          <div>
            <div className="tick-label-accent mb-2">Divergent</div>
            <p className="text-charcoal">Gap 9–20 pts. Views are drifting. Time for a conversation about what each side is measuring.</p>
          </div>
          <div>
            <div className="tick-label-accent mb-2">Disconnected</div>
            <p className="text-charcoal">Gap {'>'} 20 pts. Director and team are describing two different realities. This is where value quietly leaks.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SessionTimeline({ participants, cascade, expected }) {
  // Build a flat, time-sorted feed of arrivals.
  const events = [
    ...participants.map(p => ({
      ts: p.created_at,
      kind: "board",
      name: p.name,
      role: p.role,
      vri: p.vri,
      id: p.id,
    })),
  ];
  // Cascade doesn't have per-submission timestamps here; skip.
  events.sort((a, b) => (a.ts || "").localeCompare(b.ts || ""));
  const total = events.length;
  const target = expected || total;
  const pct = target ? Math.min(100, Math.round((total / target) * 100)) : 100;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8" data-testid="session-timeline">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="tick-label-accent">Session Timeline</div>
          <div className="display-serif text-2xl mt-1">Live activity feed</div>
        </div>
        <div className="tick-label">{total} of {target}{expected ? "" : ""} responses</div>
      </div>

      {/* Progress bar */}
      <div className="h-px bg-oxford/10 relative mb-8">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="absolute top-0 left-0 h-px bg-orange" />
      </div>

      <ol className="relative border-l border-oxford/15 pl-6 space-y-4">
        {events.slice().reverse().map((e, i) => (
          <motion.li
            key={e.id}
            layout
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: i * 0.02, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
            data-testid={`timeline-event-${i}`}
          >
            <span className="absolute -left-[27px] top-2 w-2.5 h-2.5 rounded-full bg-orange" />
            <div className="flex items-baseline justify-between">
              <div>
                <span className="text-oxford">{e.name}</span>
                {e.role && <span className="text-charcoal"> · {e.role}</span>}
              </div>
              <div className="font-mono text-lg text-oxford">{e.vri}</div>
            </div>
            <div className="tick-label mt-0.5">{(e.ts || "").slice(11, 16)} · {(e.ts || "").slice(0, 10)}</div>
          </motion.li>
        ))}
        {events.length === 0 && <li className="tick-label">No responses yet.</li>}
      </ol>
    </section>
  );
}


function CascadeCard({ c, idx }) {
  const label = c.cascade_label;
  const labelColour =
    label === "Aligned" ? "text-orange" :
    label === "Divergent" ? "text-slate" :
    label === "Disconnected" ? "text-heritage" :
    "text-charcoal";
  const barColour =
    label === "Aligned" ? "#E86A20" :
    label === "Divergent" ? "#6D82B4" :
    label === "Disconnected" ? "#B23A1A" :
    "#3B4A6B";
  const ownVri = c.own_vri;
  const teamVri = c.team_avg_vri;
  const gap = c.alignment_gap;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: 0.05 * idx, ease: [0.22, 1, 0.36, 1] }}
      className="border border-oxford/15 bg-linen p-8"
      data-testid={`cascade-card-${idx}`}
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="display-serif text-2xl md:text-3xl text-oxford">{c.name}</div>
          <div className="tick-label mt-1">{c.role || "Director"}</div>
        </div>
        <div className={`tick-label-accent ${labelColour}`}>{label}</div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4">
        <div>
          <div className="tick-label">Director VRI</div>
          <div className="font-mono text-3xl text-oxford mt-2" data-testid={`cascade-own-${idx}`}>{ownVri ?? "—"}</div>
        </div>
        <div>
          <div className="tick-label">Team avg</div>
          <div className="font-mono text-3xl text-oxford mt-2" data-testid={`cascade-team-${idx}`}>{teamVri ?? "—"}</div>
          <div className="tick-label mt-1">n = {c.team_count}</div>
        </div>
        <div>
          <div className="tick-label-accent">Gap</div>
          <div className={`font-mono text-3xl mt-2 ${labelColour}`} data-testid={`cascade-gap-${idx}`}>
            {gap == null ? "—" : gap}
          </div>
        </div>
      </div>

      {/* Visual alignment strip */}
      {(ownVri != null || teamVri != null) && (
        <div className="mt-6">
          <div className="relative h-8 bg-linen border border-oxford/10 overflow-hidden">
            {ownVri != null && (
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${(ownVri / 125) * 100}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 + 0.05 * idx }}
                className="absolute top-0 left-0 h-full opacity-90"
                style={{ background: "#0E2148" }}
              />
            )}
            {teamVri != null && (
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${(teamVri / 125) * 100}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + 0.05 * idx }}
                className="absolute bottom-0 left-0 h-1/2"
                style={{ background: barColour }}
              />
            )}
          </div>
          <div className="flex justify-between mt-2">
            <span className="tick-label">0</span>
            <span className="tick-label">125</span>
          </div>
        </div>
      )}

      {/* Per-dimension deltas */}
      {c.dimension_compare && c.dimension_compare.length > 0 && teamVri != null && (
        <div className="mt-6 pt-6 border-t border-oxford/10">
          <div className="tick-label mb-3">Per-dimension delta (director vs team)</div>
          <div className="grid grid-cols-5 gap-2">
            {c.dimension_compare.map((d) => {
              const delta = d.delta ?? 0;
              const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "·";
              const colour = delta > 0 ? "text-oxford" : delta < 0 ? "text-heritage" : "text-charcoal";
              return (
                <div key={d.dimension} className="text-center">
                  <div className="tick-label text-[9px]">{d.dimension.split(" ")[0]}</div>
                  <div className={`font-mono text-sm ${colour} mt-1`}>{arrow} {Math.abs(delta)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {teamVri == null && (
        <div className="mt-6 text-xs text-charcoal italic">
          {ownVri == null
            ? "Awaiting the director's own submission — and their team."
            : "Awaiting direct-report responses. Forward the invitation to activate the cascade."}
        </div>
      )}
    </motion.div>
  );
}
